import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import { SET_PART_COUNT_MULTIPLIER } from 'lib/api/mutations';
import {
  actionOptimisticActivitySetUpdate,
  actionCanonicalActivitySetUpdate,
} from 'lib/actions';
import { getMachine } from 'lib/selectors/getMachine';
import { getLatestActivitySet } from 'lib/selectors/getLatestActivitySet';
import { getCurrentWorkOrderOp } from 'lib/selectors/getCurrentWorkOrderOp';
import { getIsApmERPJobFlow } from 'lib/selectors/getIsApmERPJobFlow';
import { throwErrorToast, throwSuccessToast } from 'lib/utils/toast';

/**
 * Operator override of the PCM on the OPEN production run for the current WOO,
 * driven from the "Current job" overview (JobInfoERP). Mirrors the
 * optimistic → canonical reconciliation JobCard uses for UPDATE_ACTIVITY_SET,
 * so the value shown on-screen updates immediately and the recent-sets poll
 * holds its write-lock until the server confirms.
 *
 * The endpoint returns only { activitySetRef, partCountMultiplier, actualParts }.
 * The activitySets reducer merges partial sets by activitySetRef, so we dispatch
 * that partial payload — but we always include workOrderOperationRef, because
 * actionOptimisticActivitySetUpdate nulls currentWorkOrderOp when the payload
 * has no WOO ref (see reducers/index.js). Omitting it would blank the current
 * op mid-edit.
 *
 * In the APM flow the WOO comes from the feed-elected currentWorkOrderOp, not
 * the activity set — the set's WOO can be stale (a different open run), which
 * would write PCM to the wrong run server-side. See getApmCurrentPartCountMultiplier.
 *
 * The returned fn accepts an options arg with an explicit workOrderOperationRef
 * so the run-details drawer can hoist its save into the always-mounted host
 * (closing the drawer used to cancel the POST). When the override targets a run
 * that isn't the Current Job's open set, we POST by WOO alone and skip the
 * activitySet-keyed optimistic dispatch; the feed refetch reconciles both cards.
 */
const useSetPartCountMultiplier = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const machine = useSelector(getMachine);
  const latestActivitySet = useSelector(getLatestActivitySet);
  const currentWorkOrderOp = useSelector(getCurrentWorkOrderOp);
  const isApmERPJobFlow = useSelector(getIsApmERPJobFlow);

  // The Current Job card's own open run. A save with no WOO override targets
  // this; a drawer save (WOO override) may target a different run.
  const { activitySetRef: cardActivitySetRef } = latestActivitySet || {};
  // The server finds the open run to write by workOrderOperationRef. In the APM
  // flow the activity set can carry a STALE WOO (the "latest activity" set, not
  // the run the operator is watching), so use the feed-elected WOO — the same
  // ref the grid/drawer/server agree on and the drawer itself posts with.
  const cardWorkOrderOperationRef =
    isApmERPJobFlow && currentWorkOrderOp?.workOrderOperationRef != null
      ? currentWorkOrderOp.workOrderOperationRef
      : latestActivitySet?.workOrderOperationRef;

  // Error handling lives in the callback's catch (below), not here: it needs
  // per-call context (whether this save was the card's own run) to decide if
  // there's an optimistic activitySet write to roll back.
  const [setPartCountMultiplier] = useMutation(SET_PART_COUNT_MULTIPLIER, {
    fetchPolicy: 'no-cache',
  });

  return useCallback(
    async (partCountMultiplier, actualParts, options = {}) => {
      // A WOO override (from the run-details drawer) may target a run that isn't
      // the Current Job's open set. Only the card's own run has an activitySetRef
      // to key the optimistic redux dispatch on.
      const overrideWorkOrderOperationRef = options.workOrderOperationRef;
      const workOrderOperationRef =
        overrideWorkOrderOperationRef ?? cardWorkOrderOperationRef;
      const isCardRun =
        overrideWorkOrderOperationRef == null ||
        String(overrideWorkOrderOperationRef) ===
          String(cardWorkOrderOperationRef);
      // The optimistic redux write keys off an activitySetRef. In the APM ERP
      // flow the run lives in the program-events feed, so there's no activity
      // set in Redux to key on (cardActivitySetRef is undefined) — the card
      // flips via its own local state and the feed refetch reconciles. So an
      // activitySetRef is an optimization, never a requirement: when we have
      // one for the card's run, do the optimistic write; otherwise POST by WOO
      // alone (the server resolves the open run from the WOO).
      const activitySetRef = isCardRun ? cardActivitySetRef : null;
      const canWriteOptimistic = isCardRun && activitySetRef != null;

      // A WOO and a machineRef are the hard requirements: the REST path is built
      // from machineRef and the server resolves the open run from the WOO. A
      // missing activitySetRef just means no local optimistic write. Guard both
      // so we never POST with machineRef: undefined (silent 404 / wrong run).
      const machineRef = machine?.machineRef;
      if (!workOrderOperationRef || machineRef == null) {
        throwErrorToast(t('Failed to update part count'));
        return false;
      }

      const resolvedActualParts =
        actualParts != null ? actualParts : partCountMultiplier;

      // Optimistic redux write only when the card's own run has an activity set
      // in Redux to key on (the drawer, and the APM feed flow, flip locally and
      // reconcile via the feed refetch instead).
      if (canWriteOptimistic) {
        dispatch(
          actionOptimisticActivitySetUpdate({
            activitySet: {
              activitySetRef,
              workOrderOperationRef,
              partCountMultiplier,
              actualParts: resolvedActualParts,
            },
          })
        );
      }

      try {
        const requestInput = {
          machineRef,
          workOrderOperationRef,
          partCountMultiplier,
          actualParts: resolvedActualParts,
        };
        const { data } = await setPartCountMultiplier({
          variables: { input: requestInput },
        });

        const confirmed = data?.setPartCountMultiplierResponse;
        // Canonical: fold in exactly what the server persisted (it clamps /
        // defaults actualParts) and release the write-lock — only when we made
        // an optimistic write to reconcile.
        if (canWriteOptimistic) {
          dispatch(
            actionCanonicalActivitySetUpdate({
              activitySet: {
                activitySetRef: confirmed?.activitySetRef ?? activitySetRef,
                workOrderOperationRef,
                partCountMultiplier:
                  confirmed?.partCountMultiplier ?? partCountMultiplier,
                actualParts: confirmed?.actualParts ?? resolvedActualParts,
              },
            })
          );
        }
        // Grid + drawer read the programEvents feed, not activitySets; refresh
        // it now so they don't lag a 10s poll behind the Current Job card.
        window.dispatchEvent(new CustomEvent('apm:opdash-refresh'));
        throwSuccessToast(t('Part count updated'));
        // Signal success so a feed-flow caller can keep its optimistic value
        // until the feed catches up (see the card's handleSave).
        return true;
      } catch (error) {
        // Roll the card's optimistic value back to what the cache held before;
        // nothing to roll back when we never made an optimistic dispatch.
        if (canWriteOptimistic) {
          dispatch(
            actionCanonicalActivitySetUpdate({
              activitySet: {
                activitySetRef,
                workOrderOperationRef,
                partCountMultiplier: latestActivitySet?.partCountMultiplier,
                actualParts: latestActivitySet?.actualParts,
              },
            })
          );
        } else {
          // Feed-only save (drawer / APM feed flow): there is no optimistic
          // activitySet write to roll back, so the grid/drawer/card would keep
          // showing the failed optimistic value until the next 10s poll. Refetch
          // the feed now so every feed-reading surface re-syncs to the server's
          // actual state — the failure counterpart to the success refresh below.
          window.dispatchEvent(new CustomEvent('apm:opdash-refresh'));
        }
        // apollo-link-rest surfaces the status on networkError.statusCode and the
        // parsed body on networkError.result. 403 = override gate off server-side.
        // A 404 has two causes, so read the field Dashboard's serviceError.js puts
        // in errors[]: 'partOperation' means the ERP never linked this operation to
        // a part and retrying cannot help; any other 404 means the run closed while
        // the editor was open, which a retry may fix.
        const statusCode = error?.networkError?.statusCode;
        const errorFields = error?.networkError?.result?.errors;
        const isNoPartOperation =
          Array.isArray(errorFields) &&
          errorFields.some((e) => {
            return e?.field === 'partOperation';
          });
        throwErrorToast(
          statusCode === 403
            ? t("Part count editing isn't enabled for this machine.")
            : statusCode === 404 && isNoPartOperation
            ? t(
                "This operation isn't linked to a part, so part counting can't be set for it."
              )
            : statusCode === 404
            ? t(
                "This production run is no longer open, so its part count can't be changed."
              )
            : t('Failed to update part count')
        );
        // Re-throw only unexpected non-Apollo failures.
        if (!error?.graphQLErrors && !error?.networkError) {
          throw error;
        }
        // Handled failure: report it so the caller can drop its optimistic
        // value. In the feed flow the server changed nothing, so the feed
        // refetch above comes back UNCHANGED and the card's reconcile effect
        // (which only fires when reconcileSource changes) never clears the
        // optimistic value — the card must reset it off this false.
        return false;
      }
    },
    [
      dispatch,
      setPartCountMultiplier,
      machine,
      latestActivitySet,
      cardActivitySetRef,
      cardWorkOrderOperationRef,
      t,
    ]
  );
};

export default useSetPartCountMultiplier;
